<template>
    <div class="workflow-rule">
        <resource-select
            :value="workflowId"
            :options="workflows"
            :loading="loadingResources"
            field="workflowId"
            :read-only="readOnly"
            hide-groups
            :check-valid-rule="checkValidRule"
            :placeholder="$t('workflowRule.Select workflow')"
            @input="$emit('input', $event)">
            <template #noData>
                <div class="no-data-message">
                    {{ noDataText }}
                </div>
            </template>
        </resource-select>
        <template v-if="workflowId">
            <template v-if="!isLocaleEnglish">
                <div class="flex-break" />
                <strong>{{ $t('Condition') }}</strong>
                <div class="flex-break" />
            </template>
            <fact-select
                :options="factOptions"
                :value="factValue"
                :read-only="readOnly"
                :check-valid-rule="checkValidRule"
                @input="$emit('input', $event)" />
            <template v-if="!isLocaleEnglish">
                <div class="flex-break" />
                <strong>{{ $t('Criteria') }}</strong>
                <div class="flex-break" />
            </template>
            <simple-select
                :options="usedOptions"
                :value="usedValue"
                preselect-first
                field="used"
                :disabled="readOnly"
                :check-valid-rule="checkValidRule"
                @input="$emit('input', $event)" />
            <component
                :is="valueComponent.component"
                v-if="valueComponent"
                v-bind="valueComponent.props"
                :read-only="readOnly"
                :check-valid-rule="checkValidRule"
                @input="$emit('input', $event)" />
        </template>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ResourceSelect from '@/segments/components/segment-editor-vue/components/resource-select.vue';
import NumberInput from '@/segments/components/segment-editor-vue/components/number-input.vue';
import FactSelect from '@/segments/components/segment-editor-vue/components/fact-select.vue';
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import RelativeDateRange from '@/segments/components/segment-editor-vue/components/relative-date-range.vue';
import SingleDate from '@/segments/components/segment-editor-vue/components/single-date.vue';
import {
    getFactValue,
    getFactOptions,
    formatFactOptions,
    getRelativeDateRangeConfig,
    getSingleDateConfig,
    getNoResourcesFoundText,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';

export default {
    name: 'WorkflowRule',
    components: {
        FactSelect,
        NumberInput,
        RelativeDateRange,
        ResourceSelect,
        SimpleSelect,
        SingleDate,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        config: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        loadingResources: {
            type: Boolean,
            default: false,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['input'],
    data () {
        return {
            usedOptions: [],
        };
    },
    computed: {
        ...mapGetters({
            featureById: 'features/featureById',
            pageById: 'pages/pageById',
        }),
        noDataText () {
            return getNoResourcesFoundText({ rule: this.rule, isMultiApp: this.isMultiApp });
        },
        workflows () {
            return this.resources
                .filter((workflow) => {
                    if (workflow.workflowSteps.length !== 2) {
                        return false;
                    }

                    const firstStepResource = this.getCountableResourceForStep(workflow.workflowSteps[0]);
                    const secondStepResource = this.getCountableResourceForStep(workflow.workflowSteps[1]);

                    return Boolean(firstStepResource) && Boolean(secondStepResource);
                })
                .map((workflow) => {
                    return {
                        workflowId: workflow.id,
                        name: workflow.name,
                        group: {}, // ensure workflow label is shown in `resource-select` dropdown
                    };
                });
        },
        workflowId () {
            return this.workflows.find((workflow) => workflow.workflowId === this.rule.workflowId);
        },
        factOptions () {
            const options = getFactOptions(this.rule, this.config.onlyAllowGuideCompatibleRules);

            return formatFactOptions(options);
        },
        factValue () {
            return getFactValue(this.rule, this.factOptions);
        },
        usedValue () {
            return this.usedOptions.find((option) => option.used === this.rule.used);
        },
        valueComponent () {
            switch (this.rule.used) {
                case 'withinLast':
                    return getRelativeDateRangeConfig(this.rule);
                case 'since':
                    return getSingleDateConfig(this.rule, 'value');
                default:
                    return false;
            }
        },
    },
    created () {
        this.usedOptions = [
            { used: 'ever', name: this.$t('workflowRule.ever') },
            { used: 'since', name: this.$t('workflowRule.since') },
            { used: 'withinLast', name: this.$t('workflowRule.within last') },
        ];
    },
    methods: {
        getCountableResourceForStep (step) {
            switch (step.countableKind) {
                case 'Feature':
                    return this.featureById(step.countableId);
                case 'Page':
                    return this.pageById(step.countableId);
            }
        },
    },
};
</script>
<style scoped lang="less">
.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
